'use client'

import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { createClient } from '@/lib/supabase/client'

export default function LoginPage() {
  const supabase = createClient()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password })
      if (authError) {
        setError(authError.message)
        setLoading(false)
        return
      }
      if (data?.session) {
        const { data: member } = await supabase
          .from('members')
          .select('role')
          .eq('auth_user_id', data.session.user.id)
          .single()
        const role = member?.role
        if (role === 'admin') {
          window.location.href = '/admin'
        } else if (role === 'tech') {
          window.location.href = '/tech'
        } else {
          window.location.href = '/dashboard'
        }
      } else {
        setError('No session created')
        setLoading(false)
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong')
      setLoading(false)
    }
  }

  const hasError = error.length > 0

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-trc-cream px-6 py-16 text-trc-navy antialiased">
      <div className="w-full max-w-[420px]">
        {/* MARK */}
        <Link
          href="/"
          aria-label="The Repair Club home"
          className="group mb-10 flex flex-col items-center gap-4 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trc-blue/60 focus-visible:ring-offset-4 focus-visible:ring-offset-trc-cream"
        >
          <Image
            src="/logo.png"
            alt=""
            width={128}
            height={128}
            priority
            className="h-32 w-32 transition-transform duration-300 ease-out-quart group-hover:-translate-y-0.5"
          />
          <div className="text-center leading-tight">
            <div className="text-[1.05rem] font-extrabold tracking-[-0.2px] text-trc-navy">
              The Repair Club
            </div>
            <div className="mt-2 flex items-center justify-center gap-2 text-[0.7rem] font-bold uppercase tracking-label text-trc-blue">
              <span className="block h-px w-5 bg-trc-blue" aria-hidden="true" />
              <span>Member Portal</span>
              <span className="block h-px w-5 bg-trc-blue" aria-hidden="true" />
            </div>
          </div>
        </Link>

        {/* CARD */}
        <div className="rounded-xl border border-trc-ash-line bg-white p-8 shadow-calm-lift sm:p-10">
          <h1 className="text-[1.1rem] font-extrabold tracking-[-0.2px] text-trc-navy">
            Welcome back.
          </h1>
          <p className="mt-2 text-[0.9rem] leading-[1.55] text-trc-navy/65">
            Sign in to your property dashboard.
          </p>

          <form onSubmit={handleLogin} className="mt-8 space-y-5" noValidate>
            <Field
              id="login-email"
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              placeholder="you@example.com"
              autoComplete="email"
              hasError={hasError}
              required
            />
            <Field
              id="login-password"
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="••••••••"
              autoComplete="current-password"
              hasError={hasError}
              required
            />

            {/* Self-serve password reset path. Lives just under the
                password field so it's discoverable in the moment a
                member is unsure whether they remember their password,
                rather than buried in the help text below. */}
            <p className="-mt-2 text-right text-[0.8rem]">
              <Link
                href="/forgot-password"
                className="font-semibold text-trc-blue underline decoration-trc-blue/30 underline-offset-2 transition-colors hover:decoration-trc-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trc-blue/60 focus-visible:ring-offset-2"
              >
                Forgot your password?
              </Link>
            </p>

            {hasError && (
              <p
                id="login-error"
                role="alert"
                className="text-[0.85rem] leading-[1.55] text-trc-signal-red"
              >
                {humanizeAuthError(error)}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-trc-blue px-8 py-[15px] text-[0.78rem] font-bold uppercase tracking-label text-white transition-all duration-200 ease-out-quart hover:-translate-y-0.5 hover:bg-trc-blue-mid hover:shadow-action-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trc-blue/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none"
            >
              {loading ? (
                <>
                  <Spinner />
                  Signing in
                </>
              ) : (
                <>
                  Sign in
                  <Arrow />
                </>
              )}
            </button>
          </form>

          <div className="mt-7 border-t border-trc-ash-line pt-5 text-center text-[0.85rem] text-trc-navy/65">
            Not a member yet?{' '}
            <Link
              href="/#plans"
              className="font-bold text-trc-blue underline decoration-trc-blue/30 underline-offset-2 transition-colors hover:decoration-trc-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trc-blue/60 focus-visible:ring-offset-2"
            >
              View plans
            </Link>
          </div>
        </div>

        {/* HELPER */}
        <p className="mt-6 text-center text-[0.78rem] text-trc-navy/55">
          Need help signing in?{' '}
          <a
            href="https://wa.me/50671588597?text=Hi%2C%20I%20need%20help%20signing%20into%20my%20account."
            className="font-bold text-trc-navy underline decoration-trc-ash-line underline-offset-2 transition-colors hover:text-trc-blue hover:decoration-trc-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trc-blue/60 focus-visible:ring-offset-2"
          >
            WhatsApp us
          </a>
        </p>
      </div>
    </main>
  )
}

/* =========================================================================
   COMPONENTS
   ========================================================================= */

function Field({
  id,
  label,
  type,
  value,
  onChange,
  placeholder,
  autoComplete,
  hasError,
  required,
}: {
  id: string
  label: string
  type: 'email' | 'password' | 'text'
  value: string
  onChange: (v: string) => void
  placeholder?: string
  autoComplete?: string
  hasError?: boolean
  required?: boolean
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-[7px] block text-[0.72rem] font-bold uppercase tracking-label text-trc-navy"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? 'login-error' : undefined}
        className={`block w-full rounded-md border-[1.5px] bg-trc-cream px-4 py-3 text-[16px] text-trc-navy placeholder:text-trc-navy/35 transition-colors duration-150 ease-out-quart focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-trc-blue/40 focus-visible:ring-offset-0 sm:text-[0.92rem] ${
          hasError
            ? 'border-trc-signal-red focus:border-trc-signal-red'
            : 'border-black/10 focus:border-trc-blue'
        }`}
      />
    </div>
  )
}

function Arrow() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3 w-3"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

function Spinner() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className="h-3.5 w-3.5 animate-spin"
    >
      <path d="M21 12a9 9 0 1 1-6.2-8.55" />
    </svg>
  )
}

/* =========================================================================
   COPY
   ========================================================================= */

function humanizeAuthError(raw: string): string {
  const m = raw.toLowerCase()
  if (m.includes('invalid login credentials') || m.includes('invalid_credentials')) {
    return 'Email or password is incorrect. Try again or message us on WhatsApp if you need a reset.'
  }
  if (m.includes('email not confirmed')) {
    return 'Please confirm your email address from the welcome message before signing in.'
  }
  if (m.includes('rate limit') || m.includes('too many')) {
    return 'Too many attempts. Wait a minute, then try again.'
  }
  if (m.includes('network')) {
    return 'Network problem. Check your connection and try again.'
  }
  return raw || 'Something went wrong. Try again.'
}
